import React, { useEffect, useMemo } from "react";
import { Message } from "ai";
import { v4 as uuid } from "uuid";
import { Converter } from "showdown";
import { toast } from "sonner";

import styles from "./styles.module.scss";
import { useEmbedMemory } from "./api";

export const MessageComponent = ({
  m,
  tenantId,
}: {
  m: Message;
  tenantId: string;
}) => {
  const { mutateAsync: embedMemory } = useEmbedMemory();

  const convertor = new Converter({
    simplifiedAutoLink: true,
    excludeTrailingPunctuationFromURLs: true,
    openLinksInNewWindow: true,
    smoothLivePreview: true,
    disableForced4SpacesIndentedSublists: true,
  });

  const convertMarkdownToHTML = (markdown: string) => {
    if (markdown) {
      return convertor?.makeHtml(markdown);
    } else {
      return "";
    }
  };

  const tools = useMemo(() => {
    if (m?.toolInvocations && m?.toolInvocations?.length > 0) {
      return m.toolInvocations.map((invocation: any) => {
        if (invocation.toolName === "snippetCreationTool") {
          return { type: "snippet", data: invocation.args.snippetData };
        } else if (invocation.toolName === "memoryCreationTool") {
          return {
            type: "memory",
            data: invocation.args.content,
            impact: invocation.args.impact,
          };
        }
        return invocation;
      });
    }
    return [];
  }, [m]);

  return (
    <>
      {(m.content || tools.length > 0) && (
        <div
          className={`w-full flex ${m.role === "user" ? " justify-end" : " "}`}
        >
          <div
            className={`
         p-2 rounded-sm relative min-w-12 w-fit text-sm
        ${
          m.role === "user" ? ` bg-black text-white` : " bg-gray-200 text-black"
        }`}
          >
            <div
              className={styles.message}
              dangerouslySetInnerHTML={{
                __html: convertMarkdownToHTML(m.content),
              }}
            />

            {tools.length > 0 && (
              <div className="mt-3 space-y-2">
                {tools.map((tool, index) => (
                  <div
                    key={index}
                    className="border border-gray-300 rounded-md p-3 bg-white text-black shadow-sm"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">
                        {"type" in tool && tool.type === "snippet"
                          ? "Snippet"
                          : "type" in tool && tool.type === "ragMemoryTool"
                          ? "RAG Memory"
                          : "Memory"}
                      </span>
                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-md text-xs"
                        onClick={async () => {
                          try {
                            await embedMemory({
                              tenantId,
                              memories:
                                "type" in tool && tool.type === "memory"
                                  ? [
                                      {
                                        id: uuid(),
                                        text: tool.data,
                                        impact: tool.impact,
                                      },
                                    ]
                                  : [],
                              snippets:
                                "type" in tool && tool.type === "snippet"
                                  ? [
                                      {
                                        id: uuid(),
                                        text: tool.data,
                                      },
                                    ]
                                  : [],
                            });
                            toast(`Successfully saved ${tool.type}`);
                          } catch (error) {
                            console.error(error);
                            try {
                              // window.quack?.proactiveTrigger();
                            } catch (e) {}
                            toast.error(`Failed to save ${tool.type}`);
                          }
                        }}
                      >
                        Save
                      </button>
                    </div>
                    <div className="text-sm break-words">
                      {"data" in tool && tool.data}
                      {"type" in tool &&
                        tool.type === "memory" &&
                        "impact" in tool &&
                        tool.impact && (
                          <div className="mt-1 text-xs text-gray-500">
                            Impact: {tool.impact} ({tool.impact})
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
